import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import {
  DocumentSubType,
  type Interrogatory,
  ServiceManner,
} from "@/briefpoint-client";
import { useJurisdictionApi } from "@/hooks/useApi";
import type {
  JurisdictionResponse,
  ViewCommunicationResponse,
} from "@/lib/types";
import { getSubTypeText } from "@/lib/utils";

export const ServiceMannerDisplay: { [key in ServiceManner]: string } = {
  [ServiceManner.Regular]: "Regular",
  [ServiceManner.InStateMail]: "In-State Mail",
  [ServiceManner.OutOfStateMail]: "Out-of-State Mail",
  [ServiceManner.Electronic]: "Electronic",
  [ServiceManner.Other]: "Fax, Express Mail, or Overnight Delivery",
  [ServiceManner.Unknown]: "Unknown",
};

// RequestType, Set/Starting Number, Date/Type of Service
function ReviewPageTwo({
  jurisdiction,
  documentSubType,
  setDocumentSubType,
  setNumber,
  setSetNumber,
  startingNumber,
  setStartingNumber,
  dateOfService,
  setDateOfService,
  methodOfService,
  setMethodOfService,
  setDueDate,
  communication,
  interrogatories,
}: {
  jurisdiction: JurisdictionResponse | undefined;
  documentSubType: DocumentSubType;
  setDocumentSubType: React.Dispatch<React.SetStateAction<DocumentSubType>>;
  setNumber: number;
  setSetNumber: React.Dispatch<React.SetStateAction<number>>;
  startingNumber: number;
  setStartingNumber: React.Dispatch<React.SetStateAction<number>>;
  dateOfService: string | undefined;
  setDateOfService: React.Dispatch<React.SetStateAction<string | undefined>>;
  methodOfService: ServiceManner | undefined;
  setMethodOfService: React.Dispatch<
    React.SetStateAction<ServiceManner | undefined>
  >;
  dueDate: string | undefined;
  setDueDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  communication: ViewCommunicationResponse[] | undefined;
  interrogatories: Interrogatory[];
}) {
  const jurisdictionApi = useJurisdictionApi();
  const [showStartingNumber, setShowStartingNumber] = useState(false);
  useEffect(() => {
    async function effect() {
      if (dateOfService && methodOfService) {
        const newDueDate = await jurisdictionApi.jurisdictionGetDueDate({
          id: jurisdiction?.id ?? "",
          serviceManner: methodOfService,
          serviceDate: dateOfService,
        });

        if (newDueDate) {
          setDueDate(newDueDate);
        }
      }
    }
    effect();
  }, [
    dateOfService,
    methodOfService,
    jurisdictionApi,
    jurisdiction,
    setDueDate,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch showStartingNumber
  useEffect(() => {
    if (
      (setNumber > 1 || startingNumber > 1) &&
      documentSubType !== DocumentSubType.FormInterrogatories
    ) {
      setShowStartingNumber(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNumber, startingNumber]);

  const types = [
    DocumentSubType.SpecialInterrogatories,
    DocumentSubType.RequestsForProduction,
    DocumentSubType.RequestsForAdmission,
  ];

  const serviceTypes = [
    ServiceManner.Electronic,
    ServiceManner.Other,
    ServiceManner.Regular,
    ServiceManner.InStateMail,
    ServiceManner.OutOfStateMail,
  ];

  /* if there are interrogatory responses, we need to hide the request type dropdown */
  const hasInterrogatoryResponse = interrogatories.some(
    (interrogatory) => Object.keys(interrogatory.response).length > 0,
  );

  return (
    <div>
      <div>
        <Form.Group>
          <label htmlFor="requestType">Request Type</label>
          {documentSubType === DocumentSubType.FormInterrogatories ? (
            <h4>
              {
                getSubTypeText(
                  DocumentSubType.FormInterrogatories,
                  jurisdiction?.id ?? "",
                ).plural
              }
            </h4>
          ) : communication?.length || hasInterrogatoryResponse ? (
            <h4>
              {
                getSubTypeText(
                  types.find((t) => t === documentSubType) as DocumentSubType,
                  jurisdiction?.id ?? "",
                ).plural
              }
            </h4>
          ) : (
            <Form.Select
              required
              value={
                documentSubType === DocumentSubType.Unknown
                  ? ""
                  : documentSubType
              }
              size="sm"
              className="mb-3"
              onChange={(event) =>
                setDocumentSubType(Number(event.currentTarget.value))
              }
            >
              <option disabled value="">
                Choose one
              </option>
              {types.map((type, index) => (
                <option key={index} value={type}>
                  {getSubTypeText(type, jurisdiction?.id ?? "").plural}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>
      </div>
      <div>
        <Form.Group>
          <label htmlFor="setNumber">Set Number</label>
          <Form.Control
            type="number"
            min="1"
            style={{ width: 80 }}
            className="mb-3"
            value={setNumber}
            size="sm"
            onChange={(event) =>
              setSetNumber(Number.parseInt(event.currentTarget.value, 10))
            }
          />
        </Form.Group>
        {showStartingNumber && (
          <Form.Group>
            <label htmlFor="startingNumber">Starting Number</label>
            <Form.Control
              type="number"
              min="1"
              style={{ width: 80 }}
              size="sm"
              value={startingNumber || ""}
              onChange={(event) =>
                setStartingNumber(
                  Number.parseInt(event.currentTarget.value, 10),
                )
              }
            />
          </Form.Group>
        )}
      </div>
      {jurisdiction?.supportsDueDates && (
        <>
          <div className="mb-3">
            <Form.Group>
              <label htmlFor="dateOfService">Date of Service (Optional)</label>
              <Form.Control
                type="date"
                value={dateOfService}
                style={{ width: "200px" }}
                size="sm"
                onChange={(e) => setDateOfService(e.target.value)}
              />
            </Form.Group>
          </div>
          <div>
            <Form.Group>
              <label htmlFor="methodOfService">
                Method of Service (Optional)
              </label>
              <Form.Select
                required
                value={methodOfService}
                onChange={(event) =>
                  setMethodOfService(Number(event.currentTarget.value))
                }
                size="sm"
              >
                <option disabled value={0}>
                  Please Select
                </option>
                {serviceTypes?.map((s, index) => (
                  <option key={index} value={s}>
                    {ServiceMannerDisplay[s]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </>
      )}
    </div>
  );
}

export default ReviewPageTwo;
